// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Breach Contributors

export interface AuditLog {
  timestamp: number;
  type: 'fetch' | 'xhr' | 'websocket';
  method: string;
  url: string;
  sameOrigin: boolean;
}

type Listener = (log: AuditLog) => void;

let active = false;
let logs: AuditLog[] = [];
let listeners: Listener[] = [];

const origFetch = globalThis.fetch.bind(globalThis);
const OrigXMLHttpRequest = globalThis.XMLHttpRequest;
const OrigWebSocket = globalThis.WebSocket;

function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, globalThis.location.href).origin === globalThis.location.origin;
  } catch {
    return false;
  }
}

function push(log: AuditLog): void {
  logs.push(log);
  for (const fn of listeners) fn(log);
}

function record(type: AuditLog['type'], method: string, url: string): void {
  push({ timestamp: Date.now(), type, method, url, sameOrigin: isSameOrigin(url) });
}

export function activateAudit(): void {
  if (active) return;
  active = true;
  logs = [];

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string' ? input : input instanceof Request ? input.url : input.href;
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    record('fetch', method, url);
    return origFetch(input, init);
  };

  globalThis.XMLHttpRequest = new Proxy(OrigXMLHttpRequest, {
    construct(target, _args) {
      const xhr = new target();
      const origOpen = xhr.open.bind(xhr);
      xhr.open = (
        method: string,
        url: string | URL,
        async?: boolean,
        username?: string | null,
        password?: string | null
      ) => {
        record('xhr', method, typeof url === 'string' ? url : url.href);
        return origOpen(method, url, async ?? true, username, password);
      };
      return xhr;
    },
  }) as typeof OrigXMLHttpRequest;

  globalThis.WebSocket = new Proxy(OrigWebSocket, {
    construct(target, args) {
      const [url, protocols] = args as [string | URL, (string | string[])?];
      const urlStr = typeof url === 'string' ? url : url.href;
      record('websocket', 'WS', urlStr);
      return new target(url, protocols);
    },
  }) as typeof OrigWebSocket;
}

export function deactivateAudit(): void {
  if (!active) return;
  active = false;
  globalThis.fetch = origFetch;
  globalThis.XMLHttpRequest = OrigXMLHttpRequest;
  globalThis.WebSocket = OrigWebSocket;
}

export function getAuditLogs(): readonly AuditLog[] {
  return Object.freeze([...logs]);
}

export function clearAuditLogs(): void {
  logs = [];
}

export function subscribeAudit(fn: Listener): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

export function getAuditStats(): { total: number; outbound: number; sameOrigin: number } {
  const total = logs.length;
  const outbound = logs.filter((l) => !l.sameOrigin).length;
  return { total, outbound, sameOrigin: total - outbound };
}
